import React, { useEffect, useState } from 'react';
import { leads as leadsApi, leadScoring } from '@/utils/api';
import { User, Check, X, Mail, Building2, Clock, ShieldCheck, FileText, ExternalLink, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  pending_approval: { bg: '#D9A44115', color: '#D9A441', label: 'Pending' },
  approved: { bg: '#3C8A5A15', color: '#3C8A5A', label: 'Approved' },
  rejected: { bg: '#B34A3A15', color: '#B34A3A', label: 'Rejected' },
};

function LeadDetail({ leadId, onClose, onUpdated }) {
  const [lead, setLead] = useState(null);
  const [approveForm, setApproveForm] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    leadsApi.get(leadId).then(r => {
      setLead(r.data);
      setApproveForm({
        project_name: r.data.company ? `${r.data.company} — Onboarding` : 'New Project',
        project_type: 'Automation',
        company_name: r.data.company || r.data.name || '',
        contact_email: r.data.email || '',
      });
    });
  }, [leadId]);

  const approve = async () => {
    setBusy(true);
    try {
      const r = await leadsApi.approve(leadId, approveForm);
      toast.success(`Client created!${r.data.temp_password ? ' Temp password: ' + r.data.temp_password : ''}`);
      onUpdated?.();
      onClose?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed');
    } finally { setBusy(false); }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await leadsApi.reject(leadId, rejectReason);
      toast.success('Lead rejected');
      onUpdated?.();
      onClose?.();
    } catch { toast.error('Failed'); } finally { setBusy(false); }
  };

  const scoreLead = async () => {
    setBusy(true);
    try {
      const r = await leadScoring.score(leadId);
      setLead(prev => ({ ...prev, scoring: r.data }));
      toast.success(`Scored ${r.data.score_total}/100`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Scoring failed'); }
    finally { setBusy(false); }
  };

  const reprocessVault = async () => {
    setBusy(true);
    try {
      const r = await leadsApi.reprocessVault(leadId);
      toast.success(`Migrated ${r.data.moved} credential(s) to vault`);
      if (r.data.skipped?.length) {
        console.log('Skipped:', r.data.skipped);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  if (!lead) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.5)' }}>
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#006080', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  const sub = lead.submission;
  const wiz = lead.wizard;
  const requested = lead.requested_project;
  const isPending = lead.status === 'pending_approval' || lead.status === 'self_signup_pending';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{ background: 'rgba(0,0,0,0.5)' }}>
      <div className="w-full max-w-3xl rounded-card my-8" style={{ background: 'var(--surface)' }}>
        <div className="p-5 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border)' }}>
          <div>
            <h2 className="font-black text-lg" style={{ color: 'var(--ink)' }}>{lead.name || lead.email || 'Lead'}</h2>
            <div className="flex items-center gap-2 mt-1">
              <span className="badge-status" style={{ background: STATUS_STYLES[lead.status]?.bg, color: STATUS_STYLES[lead.status]?.color }}>
                {STATUS_STYLES[lead.status]?.label || lead.status}
              </span>
              <p className="text-xs" style={{ color: 'var(--muted)' }}>{new Date(lead.created_at).toLocaleString()}</p>
            </div>
          </div>
          <button onClick={onClose} style={{ color: 'var(--muted)' }}><X size={18} /></button>
        </div>

        <div className="p-5 space-y-5">
          {/* Contact */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 rounded-btn" style={{ background: 'var(--bg)' }}>
              <div className="flex items-center gap-1.5 text-xs mb-1" style={{ color: 'var(--muted)' }}><Mail size={11} /> Email</div>
              <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{lead.email || '—'}</p>
            </div>
            <div className="p-3 rounded-btn" style={{ background: 'var(--bg)' }}>
              <div className="flex items-center gap-1.5 text-xs mb-1" style={{ color: 'var(--muted)' }}><Building2 size={11} /> Company</div>
              <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{lead.company || '—'}</p>
            </div>
            <div className="p-3 rounded-btn" style={{ background: 'var(--bg)' }}>
              <div className="flex items-center gap-1.5 text-xs mb-1" style={{ color: 'var(--muted)' }}><FileText size={11} /> Source</div>
              <p className="text-sm font-semibold" style={{ color: 'var(--ink)' }}>{wiz?.title || (lead.source === 'self_signup' ? 'Self sign-up' : lead.slug || '—')}</p>
            </div>
          </div>

          {/* Requested project (self-signup leads) */}
          {requested && (
            <div className="p-3 rounded-btn" style={{ background: '#E6F1F4', border: '1px solid #B8D6DF' }} data-testid="lead-requested-project">
              <p className="text-xs font-bold uppercase mb-2" style={{ color: '#006080' }}>Requested project</p>
              <p className="text-sm font-bold" style={{ color: 'var(--ink)' }}>{requested.name}</p>
              <div className="flex gap-3 text-xs mt-1" style={{ color: 'var(--muted)' }}>
                <span>Type: <b>{requested.project_type}</b></span>
                {requested.budget_range && <span>Budget: <b>{requested.budget_range}</b></span>}
              </div>
              <p className="text-sm mt-2 whitespace-pre-wrap" style={{ color: 'var(--ink)' }}>{requested.description}</p>
            </div>
          )}

          {/* AI Scoring */}
          <div className="p-3 rounded-btn flex items-center justify-between" style={{ background: lead.scoring ? '#5B2D8E10' : 'var(--bg)', border: lead.scoring ? '1px solid #5B2D8E30' : '1px dashed var(--border)' }}>
            <div className="flex items-center gap-3">
              <Sparkles size={14} style={{ color: '#5B2D8E' }} />
              {lead.scoring ? (
                <div>
                  <p className="text-sm font-bold" style={{ color: 'var(--ink)' }}>
                    Score: <span style={{ color: '#5B2D8E' }}>{lead.scoring.score_total}/100</span>
                    {lead.scoring.recommendation && <span className="ml-2 badge-status" style={{ background: '#5B2D8E15', color: '#5B2D8E' }}>{lead.scoring.recommendation}</span>}
                  </p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--muted)' }}>{lead.scoring.rationale}</p>
                </div>
              ) : (
                <p className="text-xs" style={{ color: 'var(--muted)' }}>Not yet scored</p>
              )}
            </div>
            <button onClick={scoreLead} disabled={busy} className="text-xs px-3 py-1.5 rounded-btn font-semibold flex items-center gap-1" style={{ background: '#5B2D8E', color: 'white' }} data-testid="score-lead-button">
              <Sparkles size={10} /> {lead.scoring ? 'Re-score' : 'Score with AI'}
            </button>
          </div>

          {/* Submission sections */}
          {sub?.data && Object.keys(sub.data).length > 0 && (
            <div>
              <h3 className="font-bold text-sm mb-2" style={{ color: 'var(--ink)' }}>Submission</h3>
              <div className="space-y-3">
                {(wiz?.sections || []).map(sec => {
                  const secVals = sub.data[sec.id] || {};
                  const entries = Object.entries(secVals).filter(([_, v]) => v !== '' && v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0));
                  if (entries.length === 0) return null;
                  return (
                    <div key={sec.id} className="p-3 rounded-btn" style={{ background: 'var(--bg)' }}>
                      <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: 'var(--muted)' }}>{sec.title}</p>
                      <div className="space-y-1.5">
                        {entries.map(([fid, val]) => {
                          const fieldDef = sec.fields.find(f => f.id === fid);
                          const label = fieldDef?.label || fid;
                          let display;
                          if (Array.isArray(val)) {
                            if (val.length && typeof val[0] === 'object' && val[0].url) {
                              display = <div>{val.map((v, i) => <a key={i} href={v.url} target="_blank" rel="noreferrer" className="block text-xs" style={{ color: '#006080' }}>🔒 {v.label || v.name || v.url}</a>)}</div>;
                            } else {
                              display = val.join(', ');
                            }
                          } else if (typeof val === 'boolean') {
                            display = val ? '✓' : '✗';
                          } else {
                            display = String(val);
                          }
                          return (
                            <div key={fid} className="grid grid-cols-3 gap-3 text-xs">
                              <span className="font-medium" style={{ color: 'var(--muted)' }}>{label}</span>
                              <span className="col-span-2" style={{ color: 'var(--ink)', whiteSpace: 'pre-wrap' }}>{display}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Vault submissions */}
          {lead.vault_submissions?.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-sm flex items-center gap-1.5" style={{ color: 'var(--ink)' }}>
                  <ShieldCheck size={14} style={{ color: '#006080' }} /> Secure credentials ({lead.vault_submissions.length})
                </h3>
                {lead.status === 'approved' && (
                  <button onClick={reprocessVault} disabled={busy} className="text-xs px-2 py-1 rounded-btn font-semibold" style={{ background: '#006080', color: 'white' }} data-testid="reprocess-vault">
                    Re-migrate to vault
                  </button>
                )}
              </div>
              <div className="space-y-1.5">
                {lead.vault_submissions.map(v => (
                  <div key={v.id} className="p-2.5 rounded-btn flex items-center justify-between" style={{ background: '#E6F1F4' }}>
                    <div>
                      <p className="text-sm font-semibold" style={{ color: '#1A2428' }}>{v.label}</p>
                      <p className="text-xs" style={{ color: '#5C6B72' }}>{v.type} · {v.submitted_at ? `submitted ${new Date(v.submitted_at).toLocaleString()}` : 'pending'}</p>
                    </div>
                    {v.submitted_at ? (
                      <span className="badge-status" style={{ background: '#3C8A5A15', color: '#3C8A5A' }}>encrypted</span>
                    ) : (
                      <span className="badge-status" style={{ background: '#D9A44115', color: '#D9A441' }}>awaiting</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-xs mt-2" style={{ color: 'var(--muted)' }}>These will be moved into the project's vault automatically on approval.</p>
            </div>
          )}

          {/* Signature */}
          {sub?.signature && (
            <div className="p-3 rounded-btn" style={{ background: 'var(--bg)' }}>
              <p className="text-xs font-bold uppercase tracking-wide mb-2" style={{ color: 'var(--muted)' }}>Signature</p>
              {sub.signature.drawn ? (
                <img src={sub.signature.drawn} alt="signature" style={{ maxHeight: 80, background: 'white', border: '1px solid var(--border)', borderRadius: 6, padding: 4 }} />
              ) : (
                <p className="text-lg" style={{ fontFamily: 'cursive', color: 'var(--ink)' }}>{sub.signature.typed}</p>
              )}
              <p className="text-xs mt-1" style={{ color: 'var(--muted)' }}>Signed {new Date(sub.signature.signed_at).toLocaleString()} · IP {sub.signature.ip}</p>
            </div>
          )}

          {/* Approve form */}
          {isPending && approveForm && (
            <div className="p-4 rounded-card" style={{ background: '#3C8A5A10', border: '1px solid #3C8A5A30' }}>
              <h3 className="font-bold text-sm mb-3" style={{ color: '#3C8A5A' }}>Approve & onboard</h3>
              <p className="text-xs mb-3" style={{ color: 'var(--muted)' }}>This will create a client account, auto-provision portal access, and spin up a project with all wizard data + credentials attached.</p>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <input className="input-agency text-sm" placeholder="Project name" value={approveForm.project_name} onChange={e => setApproveForm({...approveForm, project_name: e.target.value})} data-testid="approve-project-name" />
                <input className="input-agency text-sm" placeholder="Project type" value={approveForm.project_type} onChange={e => setApproveForm({...approveForm, project_type: e.target.value})} />
                <input className="input-agency text-sm" placeholder="Company name" value={approveForm.company_name} onChange={e => setApproveForm({...approveForm, company_name: e.target.value})} />
                <input className="input-agency text-sm" placeholder="Client login email" type="email" value={approveForm.contact_email} onChange={e => setApproveForm({...approveForm, contact_email: e.target.value})} data-testid="approve-email" />
              </div>
              <div className="flex gap-2">
                <button className="flex-1 py-2 text-sm rounded-btn text-white font-semibold" style={{ background: '#3C8A5A' }} onClick={approve} disabled={busy || !approveForm.contact_email} data-testid="approve-lead-button">
                  <Check size={14} className="inline mr-1" /> Approve & Create Client
                </button>
              </div>
            </div>
          )}

          {isPending && (
            <div className="p-4 rounded-card" style={{ background: '#B34A3A10', border: '1px solid #B34A3A30' }}>
              <h3 className="font-bold text-sm mb-3" style={{ color: '#B34A3A' }}>Reject</h3>
              <input className="input-agency text-sm mb-3" placeholder="Reason (optional, internal)" value={rejectReason} onChange={e => setRejectReason(e.target.value)} />
              <button className="w-full py-2 text-sm rounded-btn font-semibold" style={{ background: 'white', color: '#B34A3A', border: '1px solid #B34A3A' }} onClick={reject} disabled={busy} data-testid="reject-lead-button">
                <X size={14} className="inline mr-1" /> Reject
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Leads() {
  const [leadsList, setLeadsList] = useState([]);
  const [allLeads, setAllLeads] = useState([]);
  const [filter, setFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(null);

  const reload = () => Promise.all([
    leadsApi.list(filter === 'all' ? null : filter).then(r => setLeadsList(r.data)),
    leadsApi.list().then(r => setAllLeads(r.data)),
  ]).catch(() => {});
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [filter]);

  const counts = {
    pending_approval: allLeads.filter(l => l.status === 'pending_approval').length,
    approved: allLeads.filter(l => l.status === 'approved').length,
    rejected: allLeads.filter(l => l.status === 'rejected').length,
    all: allLeads.length,
  };

  return (
    <div className="p-6 animate-fade-in" data-testid="leads-page">
      <h1 className="text-xl font-black mb-4" style={{ color: 'var(--ink)' }}>Leads & Onboarding</h1>

      <div className="flex gap-2 mb-4">
        {[{ k: 'all', label: 'All' }, { k: 'pending_approval', label: 'Pending' }, { k: 'approved', label: 'Approved' }, { k: 'rejected', label: 'Rejected' }].map(t => (
          <button key={t.k} onClick={() => setFilter(t.k)} className="text-xs px-3 py-1.5 rounded-btn font-semibold transition-colors flex items-center gap-1.5" style={{ background: filter === t.k ? '#006080' : 'var(--bg)', color: filter === t.k ? 'white' : 'var(--muted)', border: '1px solid var(--border)' }} data-testid={`filter-${t.k}`}>
            {t.label}
            <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ background: filter === t.k ? 'rgba(255,255,255,0.2)' : 'var(--border)', color: filter === t.k ? 'white' : 'var(--ink)' }}>
              {counts[t.k] ?? 0}
            </span>
          </button>
        ))}
      </div>

      {leadsList.length === 0 ? (
        <div className="card-base p-8 text-center">
          <User size={32} style={{ color: 'var(--border)', margin: '0 auto 12px' }} />
          <p className="text-sm" style={{ color: 'var(--muted)' }}>No leads yet. Share your wizard URL to start capturing.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {leadsList.map(lead => (
            <div key={lead.id} className="card-interactive p-3" onClick={() => setSelectedId(lead.id)} data-testid={`lead-${lead.id}`}>
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="font-bold text-sm" style={{ color: 'var(--ink)' }}>{lead.name || lead.email || 'Unknown'}</p>
                    <span className="badge-status" style={{ background: STATUS_STYLES[lead.status]?.bg, color: STATUS_STYLES[lead.status]?.color }}>
                      {STATUS_STYLES[lead.status]?.label || lead.status}
                    </span>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--muted)' }}>{lead.email} · {lead.company || 'no company'}</p>
                </div>
                <div className="flex items-center gap-1 text-xs" style={{ color: 'var(--muted)' }}>
                  <Clock size={11} /> {new Date(lead.created_at).toLocaleDateString()}
                  <ExternalLink size={11} className="ml-2" />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedId && (
        <LeadDetail leadId={selectedId} onClose={() => setSelectedId(null)} onUpdated={reload} />
      )}
    </div>
  );
}
